/**
 * Notification Service
 * Handles all notification operations with full CRUD functionality
 */

import { Injectable, signal, computed, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { AuthService } from '../../services/auth.service';
import {
  INotification,
  INotificationPayload,
  INotificationFilter,
  NotificationStatus,
  NotificationType
} from '../models/models-notification';

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);
  private readonly notificationsSignal = signal<INotification[]>([
    {
      id: 1,
      title: 'Nueva reserva: Cancha 3',
      description: 'Se ha registrado una nueva reserva para el día 15',
      type: NotificationType.INFO,
      status: NotificationStatus.UNREAD,
      timestamp: new Date(),
      read: false,
      pinned: false
    },
    {
      id: 2,
      title: 'Mantenimiento programado',
      description: 'Mantenimiento de canchas el próximo viernes',
      type: NotificationType.WARNING,
      status: NotificationStatus.UNREAD,
      timestamp: new Date(Date.now() - 3600000),
      read: false,
      pinned: false
    }
  ]);

  private readonly filterSignal = signal<INotificationFilter>({});
  private readonly notificationIdCounter = signal(3);

  readonly notifications = this.notificationsSignal.asReadonly();
  readonly filter = this.filterSignal.asReadonly();

  readonly unreadCount = computed(() =>
    this.notificationsSignal().filter(n => !n.read).length
  );

  /**
   * Load notifications from the backend API
   */
  loadNotifications(): Observable<INotification[]> {
    if (environment.useMockData) {
      return of(this.notificationsSignal());
    }

    const token = this.auth.getToken();
    const headers = { Authorization: `Bearer ${token}` };
    const url = `${environment.apiUrl}/adminNotificationsController`;
    const params = { page: '0', size: '20' };

    return this.http.get<any>(url, { headers, params }).pipe(
      map(res => {
        const items = (res && res.content) || [];
        const mapped = items.map((item: any) => {
          let typeMapped = NotificationType.INFO;
          if (item.type) {
            const typeStr = item.type.toLowerCase();
            if (typeStr === 'success') typeMapped = NotificationType.SUCCESS;
            else if (typeStr === 'warning') typeMapped = NotificationType.WARNING;
            else if (typeStr === 'error' || typeStr === 'danger') typeMapped = NotificationType.ERROR;
          }
          const readVal = item.read !== undefined ? item.read : (item.isRead !== undefined ? item.isRead : false);
          return {
            id: item.id,
            title: item.title || '',
            description: item.message || '',
            type: typeMapped,
            status: readVal ? NotificationStatus.READ : NotificationStatus.UNREAD,
            read: !!readVal,
            pinned: !!item.pinned,
            timestamp: item.createdAt ? new Date(item.createdAt) : new Date(),
            actionUrl: ''
          };
        });
        this.notificationsSignal.set(mapped);
        return mapped;
      }),
      catchError(err => {
        console.error('Error loading notifications:', err);
        return of(this.notificationsSignal());
      })
    );
  }

  readonly filteredNotifications = computed(() => {
    const notifications = this.notificationsSignal();
    const filter = this.filterSignal();

    return notifications.filter(notification => {
      if (filter.read !== undefined && notification.read !== filter.read) {
        return false;
      }
      if (filter.status && notification.status !== filter.status) {
        return false;
      }
      if (filter.type && notification.type !== filter.type) {
        return false;
      }
      if (filter.search) {
        const searchLower = filter.search.toLowerCase();
        return (
          notification.title.toLowerCase().includes(searchLower) ||
          notification.description?.toLowerCase().includes(searchLower)
        );
      }
      return true;
    });
  });

  /**
   * Add a new notification
   */
  addNotification(payload: INotificationPayload): INotification {
    const id = this.notificationIdCounter() + 1;
    this.notificationIdCounter.set(id);

    const notification: INotification = {
      id,
      timestamp: new Date(),
      status: NotificationStatus.UNREAD,
      type: payload.type || NotificationType.INFO,
      read: false,
      ...payload
    };

    this.notificationsSignal.update(current => [notification, ...current]);
    return notification;
  }

  /**
   * Mark notification as read
   */
  markAsRead(id: number): void {
    this.notificationsSignal.update(current =>
      current.map(n =>
        n.id === id
          ? { ...n, read: true, status: NotificationStatus.READ }
          : n
      )
    );

    if (!environment.useMockData) {
      const token = this.auth.getToken();
      const headers = { Authorization: `Bearer ${token}` };
      const url = `${environment.apiUrl}/adminNotificationsController/${id}/read`;
      this.http.put(url, {}, { headers }).subscribe({
        error: (err) => console.error(`Error marking notification ${id} as read:`, err)
      });
    }
  }

  /**
   * Mark all notifications as read
   */
  markAllAsRead(): void {
    this.notificationsSignal.update(current =>
      current.map(n => ({ ...n, read: true, status: NotificationStatus.READ }))
    );

    if (!environment.useMockData) {
      const token = this.auth.getToken();
      const headers = { Authorization: `Bearer ${token}` };
      const url = `${environment.apiUrl}/adminNotificationsController/read-all`;
      this.http.put(url, {}, { headers }).subscribe({
        error: (err) => console.error('Error marking all notifications as read:', err)
      });
    }
  }

  /**
   * Delete notification
   */
  deleteNotification(id: number): void {
    this.notificationsSignal.update(current =>
      current.filter(n => n.id !== id)
    );
  }

  /**
   * Pin/Unpin notification
   */
  togglePin(id: number): void {
    this.notificationsSignal.update(current =>
      current.map(n =>
        n.id === id ? { ...n, pinned: !n.pinned } : n
      )
    );
  }

  /**
   * Archive notification
   */
  archiveNotification(id: number): void {
    this.notificationsSignal.update(current =>
      current.map(n =>
        n.id === id ? { ...n, status: NotificationStatus.ARCHIVED } : n
      )
    );
  }

  /**
   * Clear all notifications
   */
  clearAll(): void {
    this.notificationsSignal.set([]);
  }

  /**
   * Get notification by ID
   */
  getNotification(id: number): INotification | undefined {
    return this.notificationsSignal().find(n => n.id === id);
  }

  /**
   * Apply filter
   */
  applyFilter(filter: INotificationFilter): void {
    this.filterSignal.set(filter);
  }

  /**
   * Clear filter
   */
  clearFilter(): void {
    this.filterSignal.set({});
  }

  /**
   * Search notifications
   */
  search(query: string): void {
    this.filterSignal.update(current => ({ ...current, search: query }));
  }
}
